import { Alert, Box, Button, CircularProgress, ListItemButton, ListItemText, Typography } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { useEffect } from 'react';
import { getAnnouncementHistory } from 'src/queries/chat/api';
import { chatKeys } from 'src/queries/chat/keys';
import type { AnnouncementReceipt } from 'src/models/chat';

export function announcementStatusLabel(status: string): string {
  switch (status) {
    case 'PENDING_PUSH': return 'Posted to Notifications · Push Pending';
    case 'SENT': return 'Push Sent';
    case 'DELIVERY_UNKNOWN': return 'Delivery Unknown';
    case 'PENDING': return 'Scheduled';
    case 'RETRYING': return 'Retry Queued';
    case 'DISPATCHING': return 'Sending';
    case 'CANCELLED': return 'Cancelled';
    case 'FAILED_AUTHORIZATION': return 'Authorization Changed';
    case 'FAILED': return 'Failed';
    default: return 'Refresh for Status';
  }
}

export default function AnnouncementHistory({ selectedId, onSelect, onReceiptRefresh }: {
  selectedId?: number;
  onSelect: (receipt: AnnouncementReceipt) => void;
  onReceiptRefresh?: (receipt: AnnouncementReceipt | null) => void;
}) {
  const query = useQuery(chatKeys.announcementHistory(), getAnnouncementHistory);
  useEffect(() => {
    if (selectedId && query.data) {
      onReceiptRefresh?.(query.data.find((receipt) => receipt.id === selectedId) ?? null);
    }
  }, [selectedId, query.data, onReceiptRefresh]);
  return <Box data-testid="announcement-history">
    <Typography variant="subtitle2">Announcement History</Typography>
    <Button size="small" onClick={() => query.refetch()} disabled={query.isFetching}>Refresh History</Button>
    {query.isLoading && <CircularProgress size={20} />}
    {query.isError && <Alert severity="error">History could not be loaded. Refresh to try again.</Alert>}
    {!query.isLoading && !query.isError && !query.data?.length && <Typography variant="body2">No saved announcements yet.</Typography>}
    {query.data?.map((receipt) => <ListItemButton key={receipt.id} selected={selectedId === receipt.id}
      onClick={() => onSelect(receipt)} data-testid={`announcement-row-${receipt.id}`}>
      <ListItemText primary={receipt.title} secondary={`Job ${receipt.jobId} · ${announcementStatusLabel(receipt.status)}`} />
    </ListItemButton>)}
    <Typography variant="subtitle2" sx={{ mt: 2 }}>Historical Chat Broadcasts</Typography>
  </Box>;
}
